export type HistoryItem = {
    id: string;
    key?: string;
    ten?: string;
    noiDung?: string;
    noiDungMoi?: string;
    ngayTao?: string;
};
import React from 'react';
import { Button, Modal } from 'antd';
import { UnitHelpers } from "@/components/MeasureIndicators/helpers/unitHelpers";
import {DownloadOutlined} from '@ant-design/icons';

import CommonJs from "@/utils/commonJs";
import { appendStaticFileAuthParams } from '@/components/common/FileAndLinkUpload';
export type CompareData = Record<string, any>;
type HistoryCompareModalProps = {
    open: boolean;
    onClose: () => void;
    oldData: CompareData;
    newData: CompareData;
    unitHelpers: UnitHelpers;
    getEmployeeOrDepartmentFullName?: (value: string) => string;
};
export const safeParseJson = (value?: string): CompareData => {
    if (!value) return {};
    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
};

const formatDateTime = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('vi-VN');
};

const formatRangeDate = (start?: string, end?: string) => {
    const s = start ? new Date(start).toLocaleDateString('vi-VN') : '';
    const e = end ? new Date(end).toLocaleDateString('vi-VN') : '';
    if (!s && !e) return '';
    return `${s}${s || e ? ' - ' : ''}${e}`;
};
type CompareField = {
    key: string;
    label: string;
    render?: (value: any, allData: CompareData, unitHelpers: UnitHelpers, getEmployeeOrDepartmentFullName?: (value: string) => string) => React.ReactNode;
};

const COMPARE_FIELDS: CompareField[] = [
    {
        key: 'TenCongViec',
        label: 'Tên công việc',
    },
    {
        key: 'TiTrong',
        label: 'Tỉ trọng',
    },
    {
        key: 'ChiTieu',
        label: 'Chỉ tiêu',
    },
    {
        key: 'IDDoLuong',
        label: 'Đơn vị',
        render: (value: any, data: CompareData, unitHelpers: UnitHelpers) => {
            return unitHelpers.getUnitLabel(data.IDDoLuong) || '#';
        },
    },
    {
        key: 'ThoiGian',
        label: 'Thời gian',
        render: (_, data) => formatRangeDate(data.NgayBatDau, data.NgayKetThuc),
    },
    {
        key: 'TanSuat',
        label: 'Báo cáo',
    },
    {
        key: 'NganSach',
        label: 'Ngân sách',
    },
    {
        key: 'MoTa',
        label: 'Mô tả',
    },
    {
        key: 'FilesDinhKem',
        label: 'Tài liệu',
        render: (value) => {
            const files = value
                ? String(value)
                    .split(',')
                    .map((x) => x.trim())
                    .filter(Boolean)
                : [];

            if (!files.length) return '';

           
            return (
                <>
                    {files.map((url, index) => {
                        const parts = String(url).split('/').filter(Boolean);
                        const fileName = decodeURIComponent(parts[parts.length - 1] || 'Tài liệu');
                        const authUrl = appendStaticFileAuthParams(url);
                        return (
                            <tr key={`${url}-${index}`}>
                                <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                    <Button type="text" size="small" icon={<DownloadOutlined />} onClick={() => {
                                        CommonJs.downloadFile(url, fileName);
                                    }} />
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                    <a
                                        href={authUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ color: '#2563eb', textDecoration: 'underline' }}
                                    >
                                        {fileName}
                                    </a>
                                </td>
                                
                            </tr>
                        );
                    })}
                  
                </>
             
            );
        },
    },
    {
        key: 'BoPhanPhuTrachID',
        label: 'Bộ phận',
        render: (value: any, data: CompareData, unitHelpers: UnitHelpers, getEmployeeOrDepartmentFullName?: (value: string) => string) => {
            return getEmployeeOrDepartmentFullName?.(value) || ''
        },
    },
    {
        key: 'NguoiPhuTrachID',
        label: 'Người phụ trách',
        render: (value: any, data: CompareData, unitHelpers: UnitHelpers, getEmployeeOrDepartmentFullName?: (value: string) => string) => {
            return getEmployeeOrDepartmentFullName?.(value) || '';
        },
    },
    {
        key: 'NguoiHoTroID',
        label: 'Người hỗ trợ',
        render: (value: any, data: CompareData, unitHelpers: UnitHelpers, getEmployeeOrDepartmentFullName?: (value: string) => string) => {
            return getEmployeeOrDepartmentFullName?.(value) || '';
        },
    },
    {
        key: 'NgayCapNhat',
        label: 'Cập nhật lúc',
        render: (value) => formatDateTime(value),
    },
];



const cellValue = (
    field: CompareField,
    data: CompareData,
    unitHelpers: UnitHelpers,
    getEmployeeOrDepartmentFullName?: (value: string) => string,
): React.ReactNode => {

    if (field.render) {
        return field.render(data[field.key], data, unitHelpers, getEmployeeOrDepartmentFullName);
    }

    const value = data[field.key];
    if (value === null || value === undefined || value === '') return '';

    return String(value);
};

export const HistoryCompareModal: React.FC<HistoryCompareModalProps> = ({
    open,
    onClose,
    oldData,
    newData,
    unitHelpers,
    getEmployeeOrDepartmentFullName,
}) => {
    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1200}
            title={null}
            centered
            styles={{
                body: { padding: 24 },
            }}
        >
            <div

                className="grid grid-cols-2 gap-10 p-4">
                <div>
                    <div className="mb-4 text-[18px] font-bold">Nội dung cũ</div>
                    <div className="space-y-0">
                        {COMPARE_FIELDS.map((field) => (
                            <div
                                key={field.key}
                                className="grid grid-cols-[170px_1fr] border-t border-slate-200 py-3"
                            >
                                <div className="font-semibold text-slate-800">{field.label} :</div>
                                <div className="font-bold text-slate-900 break-words "

                                >
                                    {cellValue(field, oldData, unitHelpers, getEmployeeOrDepartmentFullName) || ''}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="mb-4 text-[18px] font-bold">Nội dung mới</div>
                    <div className="space-y-0">
                        {COMPARE_FIELDS.map((field) => (
                            <div
                                key={field.key}
                                className="grid grid-cols-[170px_1fr] border-t border-slate-200 py-3"
                            >
                                <div className="font-semibold text-slate-800">{field.label} :</div>
                                <div className="font-bold text-slate-900 break-words">
                                    {cellValue(field, newData, unitHelpers, getEmployeeOrDepartmentFullName) || ''}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
};
